// frontend/src/context/AppContext.tsx
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User, WatchlistItem, FavoriteItem } from '../types';
import { authAPI, moviesAPI, userAPI } from '../services/api';


interface AppState {
  currentUser: User | null;
  likedMovies: number[];
  watchlist: number[];
  watchedMovies: number[];
  isLoginModalOpen: boolean;
  isLoading: boolean;
}

interface AppContextType {
  state: AppState;

  // Movie actions
  toggleLike: (movieId: number) => void;
  toggleWatchlist: (movieId: number) => void;
  toggleWatched: (movieId: number) => void;

  // Auth actions
  openLoginModal: () => void;
  closeLoginModal: () => void;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  signup: (name: string, username: string, email: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  updateProfile: (updates: Partial<User>) => Promise<void>;

  // Data loading
  loadUserData: () => Promise<void>;

  // Utility
  isMovieLiked: (movieId: number) => boolean;
  isInWatchlist: (movieId: number) => boolean;
  isMovieWatched: (movieId: number) => boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const initialState: AppState = {
  currentUser: null,
  likedMovies: [],
  watchlist: [],
  watchedMovies: [],
  isLoginModalOpen: false,
  isLoading: true,
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppState>(initialState);

  // ── Check auth on app start ──
  useEffect(() => {
  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await loadUserData();
      } catch (error) {
        console.error('Failed to load user data:', error);
        localStorage.removeItem('token');
      }
    }
    // Always set loading to false whether there's a token or not
    setState(prev => ({ ...prev, isLoading: false }));
  };
  checkAuth();
}, []);
  // ─────────────────────────────────────────────
  // USER DATA LOADING
  // ─────────────────────────────────────────────
  const loadUserData = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      // Load user profile
      const profileRes = await authAPI.getProfile();
      if (profileRes.data.success) {
        setState(prev => ({ ...prev, currentUser: profileRes.data.data }));
      }

      // Load favorites
      try {
        const favoritesRes = await userAPI.getFavorites();
        if (favoritesRes.data.success) {
          const favoriteMovieIds = favoritesRes.data.data
            .map((fav: FavoriteItem & { id?: number }) => fav.movieId ?? fav.id)
            .filter((id: number | undefined): id is number => typeof id === 'number');
          setState(prev => ({ ...prev, likedMovies: favoriteMovieIds }));
        }
      } catch (error) {
        console.warn('Failed to load favorites:', error);
      }

      // Load watchlist
      try {
        const watchlistRes = await userAPI.getWatchlist();
        if (watchlistRes.data.success) {
          const watchlistIds = watchlistRes.data.data
            .map((item: WatchlistItem & { id?: number }) => item.movieId ?? item.id)
            .filter((id: number | undefined): id is number => typeof id === 'number');
          setState(prev => ({ ...prev, watchlist: watchlistIds }));
        }
      } catch (error) {
        console.warn('Failed to load watchlist:', error);
      }

      // Load watched movies (Memory Lane)
      try {
        const watchedRes = await userAPI.getWatched();
        if (watchedRes.data.success) {
          const watchedIds = watchedRes.data.data
            .map((item: WatchlistItem & { id?: number }) => item.movieId ?? item.id)
            .filter((id: number | undefined): id is number => typeof id === 'number');
          setState(prev => ({ ...prev, watchedMovies: watchedIds }));
        }
      } catch (error) {
        console.warn('Failed to load watched movies:', error);
      }

    } catch (error) {
      console.error('Failed to load user data:', error);
      throw error;
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  // ─────────────────────────────────────────────
  // MOVIE ACTIONS
  // ─────────────────────────────────────────────
  const toggleLike = async (movieId: number) => {
    if (!state.currentUser) { openLoginModal(); return; }
    try {
      await moviesAPI.toggleFavorite(movieId);
      setState(prev => ({
        ...prev,
        likedMovies: prev.likedMovies.includes(movieId)
          ? prev.likedMovies.filter(id => id !== movieId)
          : [...prev.likedMovies, movieId],
      }));
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const toggleWatchlist = async (movieId: number) => {
    if (!state.currentUser) { openLoginModal(); return; }
    try {
      await moviesAPI.toggleWatchlist(movieId);
      setState(prev => ({
        ...prev,
        watchlist: prev.watchlist.includes(movieId)
          ? prev.watchlist.filter(id => id !== movieId)
          : [...prev.watchlist, movieId],
      }));
    } catch (error) {
      console.error('Failed to toggle watchlist:', error);
    }
  };

  const toggleWatched = async (movieId: number) => {
    if (!state.currentUser) { openLoginModal(); return; }
    try {
      await moviesAPI.toggleWatched(movieId);
      setState(prev => ({
        ...prev,
        watchedMovies: prev.watchedMovies.includes(movieId)
          ? prev.watchedMovies.filter(id => id !== movieId)
          : [movieId, ...prev.watchedMovies],
      }));
    } catch (error) {
      console.error('Failed to toggle watched movie:', error);
    }
  };

  // ─────────────────────────────────────────────
  // AUTH ACTIONS
  // ─────────────────────────────────────────────
  const openLoginModal = () => setState(prev => ({ ...prev, isLoginModalOpen: true }));
  const closeLoginModal = () => setState(prev => ({ ...prev, isLoginModalOpen: false }));

  const login = async (email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const response = await authAPI.login({ email, password });
      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        await loadUserData();
        closeLoginModal();
        return { success: true, message: 'Login successful!' };
      }
      return { success: false, message: response.data.message || 'Login failed' };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Login failed. Please try again.' };
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const signup = async (name: string, username: string, email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const response = await authAPI.register({ name, username, email, password });
      if (response.data.success) {
        return await login(email, password);
      }
      return { success: false, message: response.data.message || 'Registration failed' };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Registration failed. Please try again.' };
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

const logout = async () => {
  try {
    await authAPI.logout();
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    localStorage.removeItem('token');
    setState({ ...initialState, isLoading: false });
  }
};

  const updateProfile = async (updates: Partial<User>) => {
    if (!state.currentUser) return;
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const response = await authAPI.updateProfile(updates);
      if (response.data.success) {
        setState(prev => ({
          ...prev,
          currentUser: { ...prev.currentUser!, ...updates },
        }));
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  // ─────────────────────────────────────────────
  // UTILITY
  // ─────────────────────────────────────────────
  const isMovieLiked = (movieId: number): boolean => state.likedMovies.includes(movieId);
  const isInWatchlist = (movieId: number): boolean => state.watchlist.includes(movieId);
  const isMovieWatched = (movieId: number): boolean => state.watchedMovies.includes(movieId);

  // ─────────────────────────────────────────────
  // CONTEXT VALUE
  // ─────────────────────────────────────────────
  const contextValue: AppContextType = {
    state,
    toggleLike,
    toggleWatchlist,
    toggleWatched,
    openLoginModal,
    closeLoginModal,
    login,
    signup,
    logout,
    updateProfile,
    loadUserData,
    isMovieLiked,
    isInWatchlist,
    isMovieWatched,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};